/**
 * exportCv — export du parcours pro en CV imprimable (→ PDF via le
 * dialogue d'impression du navigateur).
 *
 * buildCvHtml : fonction pure (testable) qui prend les données déjà
 * mappées (props de <ParcoursPro/>) et retourne un document HTML complet,
 * autonome et print-friendly.
 * openCvPrintWindow : ouvre le document dans une fenêtre et lance print().
 */

import { formatDuration, formatPeriod, totalExperienceMonths } from './ParcoursPro'

/** Échappe une valeur pour insertion dans du HTML. */
export function esc(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

const CV_STYLE = `
  * { box-sizing: border-box; margin: 0; }
  body { font: 13px/1.5 'Inter', 'Helvetica Neue', Arial, sans-serif; color: #1a1a2e; padding: 40px 48px; max-width: 800px; margin: 0 auto; }
  h1 { font-size: 26px; letter-spacing: -0.02em; }
  .contact { color: #666; margin-top: 2px; }
  .headline { color: #444; margin-top: 6px; font-size: 14px; }
  h2 { font-size: 13px; text-transform: uppercase; letter-spacing: 0.08em; color: #4a5bb0; border-bottom: 1px solid #d8dcf0; padding-bottom: 4px; margin: 26px 0 12px; }
  .item { margin-bottom: 14px; page-break-inside: avoid; }
  .item-head { display: flex; justify-content: space-between; gap: 12px; }
  .item-title { font-weight: 600; }
  .item-period { color: #666; white-space: nowrap; font-size: 12px; }
  .item-sub { color: #444; }
  .item-tags { color: #666; font-size: 12px; margin-top: 2px; }
  .skills { display: flex; flex-wrap: wrap; gap: 6px; }
  .skill { border: 1px solid #ccc; border-radius: 4px; padding: 2px 8px; font-size: 12px; }
  .skill-type { color: #888; }
  @media print { body { padding: 0; } }
`

/**
 * Construit le CV HTML depuis les données mappées.
 * @param {{experiences:Array, formations:Array, skills:Array}} data
 * @param {{name?:string, email?:string}} user
 * @param {Date} today
 */
export function buildCvHtml(data, user = {}, today = new Date()) {
  const { experiences = [], formations = [], skills = [] } = data
  const current = experiences.find((e) => e.end === null)
  const totalM = totalExperienceMonths(experiences, today)

  const headline = current
    ? `${current.title} · ${current.company}${totalM ? ` — ${formatDuration(totalM)} d'expérience` : ''}`
    : totalM
      ? `${formatDuration(totalM)} d'expérience`
      : ''

  const expHtml = [...experiences]
    .sort((a, b) => (a.end === null ? -1 : b.end === null ? 1 : String(b.start).localeCompare(String(a.start))))
    .map(
      (e) => `
      <div class="item">
        <div class="item-head">
          <span class="item-title">${esc(e.title)}</span>
          <span class="item-period">${esc(formatPeriod(e.start, e.end))}</span>
        </div>
        <div class="item-sub">${esc(e.company)}${e.location ? ` · ${esc(e.location)}` : ''}${e.contract ? ` · ${esc(e.contract)}` : ''}</div>
        ${e.tags?.length ? `<div class="item-tags">${e.tags.map(esc).join(' · ')}</div>` : ''}
      </div>`,
    )
    .join('')

  const eduHtml = [...formations]
    .sort((a, b) => String(b.start).localeCompare(String(a.start)))
    .map(
      (f) => `
      <div class="item">
        <div class="item-head">
          <span class="item-title">${esc(f.title)}</span>
          <span class="item-period">${esc(formatPeriod(f.start, f.end))}</span>
        </div>
        <div class="item-sub">${esc(f.school)}${f.type ? ` · ${esc(f.type)}` : ''}</div>
      </div>`,
    )
    .join('')

  const skillsHtml = skills
    .filter((s) => !s.suggested)
    .map((s) => `<span class="skill">${esc(s.name)}${s.type ? ` <span class="skill-type">· ${esc(s.type)}</span>` : ''}</span>`)
    .join('')

  const title = user.name ? `CV — ${user.name}` : 'CV — Mon parcours pro'

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8" />
<title>${esc(title)}</title>
<style>${CV_STYLE}</style>
</head>
<body>
  <header>
    <h1>${esc(user.name || 'Mon parcours pro')}</h1>
    ${user.email ? `<div class="contact">${esc(user.email)}</div>` : ''}
    ${headline ? `<div class="headline">${esc(headline)}</div>` : ''}
  </header>
  ${experiences.length ? `<h2>Expériences</h2>${expHtml}` : ''}
  ${formations.length ? `<h2>Formations et certifications</h2>${eduHtml}` : ''}
  ${skillsHtml ? `<h2>Compétences</h2><div class="skills">${skillsHtml}</div>` : ''}
</body>
</html>`
}

/**
 * Ouvre le CV dans une fenêtre dédiée et déclenche l'impression
 * (l'utilisateur choisit "Enregistrer en PDF" s'il le souhaite).
 * @returns {boolean} false si le popup a été bloqué
 */
export function openCvPrintWindow(html) {
  const win = window.open('', '_blank', 'width=900,height=1100')
  if (!win) return false
  win.document.write(html)
  win.document.close()
  win.focus()
  // Laisse le temps au document (et aux polices) de se poser avant print.
  setTimeout(() => {
    try {
      win.print()
    } catch (e) {
      // fenêtre fermée entre-temps : rien à faire
    }
  }, 400)
  return true
}
